import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Product, ProductListConfig } from "../../types/types";
import {
  createProductListEventDetail,
  dispatchCustomEvent,
} from "../../utils/events/dispatchEvent";
import { useVariant } from "../VariantContext/VariantContext";
import { EventName, productCardEvents } from "./productCardEvents";

type ProductCardEventContextType = {
  product: Product;
  config: ProductListConfig;
  index: number;
  eventsRef: RefObject<HTMLElement | null>;
  instanceId: string;
  variantId?: string;
  skuId?: string;
};

const ProductCardEventContext =
  createContext<ProductCardEventContextType | null>(null);

type Props = ProductCardEventContextType & {
  children: ReactNode;
};

export function ProductCardEventProvider(props: Props): React.JSX.Element {
  const { variant } = useVariant();
  const {
    children,
    product,
    index,
    instanceId,
    eventsRef,
    ...nonChildrenProps
  } = props;
  const hasMounted = useRef(false);
  const variantId = variant?.id;
  const skuId = variant?.aliases?.skuId;

  const latestCardDetails = useRef({ product, index, instanceId, eventsRef });
  latestCardDetails.current = { product, index, instanceId, eventsRef };

  useEffect(
    function dispatchVariantChangedEvent() {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }

      if (!variantId) {
        return;
      }

      const { product, index, instanceId, eventsRef } =
        latestCardDetails.current;

      dispatchCustomEvent(
        eventsRef,
        productCardEvents.variantChanged,
        createProductListEventDetail({
          type: productCardEvents.variantChanged,
          product,
          index,
          instanceId,
          variantId,
          skuId,
        }),
      );
    },
    [variantId, skuId],
  );

  return (
    <ProductCardEventContext.Provider
      value={{
        ...nonChildrenProps,
        product,
        index,
        instanceId,
        eventsRef,
        variantId,
        skuId,
      }}
    >
      {children}
    </ProductCardEventContext.Provider>
  );
}

export function useProductCardEvent() {
  const context = useContext(ProductCardEventContext);

  if (!context) {
    throw new Error(
      "useProductCardEvent must be used within a ProductCardEventProvider",
    );
  }

  const { product, index, variantId, skuId, instanceId } = context;

  const standardDetail = createProductListEventDetail({
    type: "",
    product,
    index,
    quantity: 1,
    instanceId,
    variantId,
    skuId,
  });

  return (type: EventName, customDetail?: Record<string, unknown>) => {
    dispatchCustomEvent(context.eventsRef, type, {
      ...standardDetail,
      ...customDetail,
    });
  };
}
